import { ArrowUpToLine, Search, SearchX } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { ROW_HEIGHT, useDensity } from '../lib/density.ts';
import type { Density } from '../lib/density.ts';
import { plural } from '../lib/format.ts';
import { useLiveOrder } from '../lib/liveOrder.ts';
import { isTypingTarget } from '../lib/platform.ts';
import { allSelected, useSelection } from '../lib/selection.ts';
import { columnAlign, nameColumn, sortsDescFirst, tableClass } from '../lib/table.ts';
import type { CellAlign } from '../lib/table.ts';
import { buildTableItems } from '../lib/tableItems.ts';
import type { TableItem } from '../lib/tableItems.ts';
import { VIRTUALIZE_ABOVE, useVirtualRows } from '../lib/virtual.ts';
import { BulkBar } from './BulkBar.tsx';
import type { BulkAction } from './BulkBar.tsx';
import { Button } from './Button.tsx';
import { Checkbox } from './Checkbox.tsx';
import { TableSortButton } from '../primitives/table.tsx';
import type { Column } from './DataTable.tsx';
import { EmptyState } from './EmptyState.tsx';
import { ErrorState } from './ErrorState.tsx';
import { Input } from './Input.tsx';
import { Kbd } from './Kbd.tsx';
import { REVEAL_GROUP, RevealSlot } from './Reveal.tsx';
import { Skeleton } from './Skeleton.tsx';

/**
 * The table at scale (DesignSystem 3.4): the same columns as DataTable, plus windowed rows
 * above 100, a sticky header, density, bulk select with a floating bar, and live rows that
 * update in place and never move while the reader is in the list ("3 new, show").
 */
export type SortState = { key: string; dir: 'asc' | 'desc' };

export interface VirtualTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  /** Accessible name of the table. */
  label: string;
  searchable?: { placeholder: string; /** Focus the search with "/" while this table is on screen. */ shortcut?: boolean };
  /** Controlled search (mirror it in the URL). */
  query?: string;
  onQueryChange?: (q: string) => void;
  /** Filter controls on the table's top edge, beside the search. */
  filters?: ReactNode;
  /** A full-width row under the toolbar: filter chips, saved views, density. */
  subbar?: ReactNode;
  /** Right of the toolbar. A function gets how many rows the search shows, for a count that matches the list. */
  toolbarEnd?: ReactNode | ((shown: number) => ReactNode);
  rowActions?: (row: T) => ReactNode;
  onRowClick?: (row: T) => void;
  rowLabel?: (row: T) => string;
  groupBy?: (row: T) => string;
  renderGroup?: (key: string, rows: T[]) => ReactNode;
  groupSort?: (a: string, b: string) => number;
  /** Height of a group header row, px. Default 32 (28 compact). */
  groupHeight?: number;
  loadMore?: { onLoad: () => void; loading: boolean; more: boolean };
  empty?: ReactNode;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void | Promise<void>;
  initialSort?: SortState;
  /** Controlled sort (mirror it in the URL). */
  sort?: SortState | null;
  onSortChange?: (s: SortState | null) => void;
  activeKey?: string | null;
  className?: string;
  frameless?: boolean;
  /** Bulk select: a checkbox column and the floating action bar. */
  selection?: { noun?: string; actions: (rows: T[]) => BulkAction[] };
  /**
   * Live data: rows keep their places while the reader is in the list; new rows wait behind
   * a "N new, show" bar. `versionOf` makes changed rows flash softly in place.
   */
  live?: { versionOf?: (row: T) => unknown };
  /** Changes when the person changes the view (filters, sort, search): held rows are released. */
  viewKey?: string;
  /** Rows above which the table windows. Default 100. */
  virtualizeAbove?: number;
  /** Row height per density, px, for rows with two lines. Default ROW_HEIGHT (40, compact 32). */
  rowHeight?: Record<Density, number>;
  /** Scroll this group's header into view (a deep link to it). */
  revealGroup?: string | null;
}

const HIDE: Record<NonNullable<Column<unknown>['hideBelow']>, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
};
const GROUP_HEIGHT: Record<Density, number> = { comfortable: 32, compact: 28 };

export function compareValues(a: string | number | null | undefined, b: string | number | null | undefined): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

type Item<T> = TableItem<T>;

export function VirtualTable<T>({
  rows,
  columns,
  rowKey,
  label,
  searchable,
  query: queryProp,
  onQueryChange,
  filters,
  subbar,
  toolbarEnd,
  rowActions,
  onRowClick,
  rowLabel,
  groupBy,
  renderGroup,
  groupSort,
  groupHeight,
  loadMore,
  empty,
  loading,
  error,
  onRetry,
  initialSort,
  sort: sortProp,
  onSortChange,
  activeKey,
  className,
  frameless,
  selection,
  live,
  viewKey,
  virtualizeAbove = VIRTUALIZE_ABOVE,
  rowHeight,
  revealGroup,
}: VirtualTableProps<T>) {
  const density = useDensity();
  const rowH = (rowHeight ?? ROW_HEIGHT)[density];
  const groupH = groupHeight ?? GROUP_HEIGHT[density];
  const [queryState, setQueryState] = useState('');
  const query = queryProp ?? queryState;
  const setQuery = onQueryChange ?? setQueryState;
  const [sortState, setSortState] = useState<SortState | null>(initialSort ?? null);
  const sort = sortProp !== undefined ? sortProp : sortState;
  const setSort = (s: SortState | null) => (onSortChange ? onSortChange(s) : setSortState(s));
  const searchRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [pointerIn, setPointerIn] = useState(false);

  useEffect(() => {
    if (!searchable?.shortcut) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      if (isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"][data-state="open"]')) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchable?.shortcut]);

  // Alignment reads the data once per data change: numbers right, everything else left.
  const aligns = useMemo(() => new Map(columns.map((c) => [c.key, columnAlign(c, rows)])), [columns, rows]);
  const alignOf = (c: Column<T>): CellAlign => aligns.get(c.key) ?? 'left';
  const nameKey = nameColumn(columns);

  // Search and sort.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = rows;
    if (q) {
      const words = q.split(/\s+/);
      out = rows.filter((r) => {
        const hay = columns.map((c) => c.text?.(r) ?? '').join(' ').toLowerCase();
        return words.every((w) => hay.includes(w));
      });
    }
    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      if (col?.sortValue) {
        const sv = col.sortValue;
        const dir = sort.dir === 'asc' ? 1 : -1;
        out = [...out].sort((a, b) => dir * compareValues(sv(a), sv(b)));
      }
    }
    return out;
  }, [rows, columns, query, sort]);

  const byKey = useMemo(() => {
    const m = new Map<string, T>();
    for (const r of visible) m.set(rowKey(r), r);
    return m;
  }, [visible, rowKey]);
  const keys = useMemo(() => [...byKey.keys()], [byKey]);

  // Selection. Ranges and Select all follow the rows on screen (grouped, held), set below.
  const displayRef = useRef<readonly string[]>(keys);
  const sel = useSelection(keys, { displayOrder: () => displayRef.current });

  // Live: hold the order while the reader is in the list.
  const versionOf = useMemo(() => {
    const v = live?.versionOf;
    return v ? (k: string) => (byKey.has(k) ? v(byKey.get(k)!) : undefined) : undefined;
  }, [live?.versionOf, byKey]);
  const [scrolled, setScrolled] = useState(false);
  const held = !!live && (pointerIn || scrolled || sel.count > 0);
  const liveOrder = useLiveOrder(keys, held, { versionOf, enabled: !!live });
  const order = live ? liveOrder.order : keys;
  const { release } = liveOrder;
  useEffect(() => {
    release();
  }, [viewKey, query, sort?.key, sort?.dir, release]);

  // Rows in display order, with group headers.
  const items = useMemo<Item<T>[]>(
    () => buildTableItems(order, byKey, { rowKey, groupBy, groupSort, headers: !!renderGroup }),
    [order, byKey, groupBy, groupSort, renderGroup, rowKey],
  );
  const rowItems = useMemo(() => items.filter((i): i is Extract<Item<T>, { kind: 'row' }> => i.kind === 'row'), [items]);
  const displayKeys = useMemo(() => rowItems.map((i) => i.key), [rowItems]);
  displayRef.current = displayKeys;
  const allShownSelected = sel.count > 0 && allSelected(displayKeys, sel.selected);
  const itemIndexOfRow = useMemo(() => {
    const m: number[] = [];
    items.forEach((it, i) => {
      if (it.kind === 'row') m.push(i);
    });
    return m;
  }, [items]);

  // Windowing.
  const heightOf = useCallback((i: number) => (items[i]?.kind === 'group' ? groupH : rowH), [items, groupH, rowH]);
  const virt = useVirtualRows({
    count: items.length,
    heightOf,
    heightKey: `${density}:${groupH}`,
    ref: bodyRef,
    enabled: items.length > virtualizeAbove,
  });
  useEffect(() => setScrolled(virt.scrolledPast > rowH), [virt.scrolledPast, rowH]);
  const { scrollToIndex } = virt;
  useEffect(() => {
    if (!revealGroup) return;
    const i = items.findIndex((it) => it.kind === 'group' && it.key === `group:${revealGroup}`);
    if (i >= 0) scrollToIndex(i, 'start');
    // Only when the target changes, not on every live update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealGroup, items.length > 0]);

  // Fixed column widths once windowed, so columns never jitter as rows come and go.
  const headRefs = useRef<Array<HTMLTableCellElement | null>>([]);
  const [widths, setWidths] = useState<number[] | null>(null);
  const columnsKey = columns.map((c) => c.key).join('|');
  useLayoutEffect(() => {
    if (!virt.enabled) {
      if (widths) setWidths(null);
      return;
    }
    if (widths) return;
    const w = headRefs.current.map((th) => th?.getBoundingClientRect().width ?? 0);
    if (w.some((x) => x > 0)) setWidths(w);
  }, [virt.enabled, widths, columnsKey, density]);
  useEffect(() => {
    const onResize = () => setWidths(null);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => setWidths(null), [columnsKey, density]);

  // Keyboard: j/k and arrows move between rows, x selects, Enter opens.
  const [focusRow, setFocusRow] = useState<number | null>(null);
  // Focus follows a keyboard move only (j/k, arrows, Home, End), once the row has rendered (it
  // may be virtualised away until the scroll lands). Never on other renders: a live update or a
  // hover card opening would pull focus back from a link in another row, a Tab loop.
  const pendingFocus = useRef(false);
  useEffect(() => {
    if (!pendingFocus.current || focusRow == null) return;
    const el = bodyRef.current?.querySelector<HTMLElement>(`[data-row-index="${focusRow}"]`);
    if (!el) return;
    pendingFocus.current = false;
    if (document.activeElement !== el) el.focus({ preventScroll: true });
  });
  const moveTo = (i: number) => {
    const n = rowItems.length;
    if (!n) return;
    const next = Math.max(0, Math.min(n - 1, i));
    const el = bodyRef.current?.querySelector<HTMLElement>(`[data-row-index="${next}"]`);
    if (el) el.focus({ preventScroll: true });
    pendingFocus.current = !el;
    setFocusRow(next);
    virt.scrollToIndex(itemIndexOfRow[next] ?? 0);
  };
  const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>, it: Extract<Item<T>, { kind: 'row' }>) => {
    if (e.target !== e.currentTarget || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === 'Enter' && onRowClick) {
      e.preventDefault();
      onRowClick(it.row);
    } else if ((e.key === 'x' || e.key === ' ') && selection) {
      e.preventDefault();
      sel.toggle(it.key, e.shiftKey);
    } else if (e.key === 'ArrowDown' || e.key === 'j') {
      e.preventDefault();
      moveTo(it.index + 1);
    } else if (e.key === 'ArrowUp' || e.key === 'k') {
      e.preventDefault();
      moveTo(it.index - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      moveTo(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      moveTo(rowItems.length - 1);
    }
  };

  const toggleSort = (c: Column<T>) => {
    if (!c.sortValue) return;
    const firstDir: 'asc' | 'desc' = sortsDescFirst(c, alignOf(c)) ? 'desc' : 'asc';
    setSort(sort?.key === c.key ? { key: c.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key: c.key, dir: firstDir });
  };

  const selCol = !!selection;
  const colCount = columns.length + (rowActions ? 1 : 0) + (selCol ? 1 : 0);
  const hasToolbar = searchable != null || filters != null || toolbarEnd != null;
  const fixed = !!widths && virt.enabled;
  const firstGutter = selCol ? 'pl-3' : 'pl-5';

  const cellClass = (c: Column<T>, ci: number) =>
    cn(
      tableClass.cell,
      c.key === nameKey && tableClass.name,
      ci === 0 ? firstGutter : 'pl-4',
      ci === columns.length - 1 && !rowActions && 'pr-5',
      c.grow ? 'w-full max-w-0' : 'w-px whitespace-nowrap',
      fixed && 'overflow-hidden text-ellipsis whitespace-nowrap',
      alignOf(c) === 'right' && tableClass.numeric,
      c.hideBelow && HIDE[c.hideBelow],
      c.className,
    );

  let body: ReactNode;
  if (loading) {
    body = Array.from({ length: 6 }, (_, i) => (
      <tr key={`sk${i}`} style={{ height: rowH }}>
        {selCol && <td className="w-px pl-5" />}
        {columns.map((c, ci) => (
          <td key={c.key} className={cn(ci === 0 ? firstGutter : 'pl-4', ci === columns.length - 1 && !rowActions && 'pr-5', c.hideBelow && HIDE[c.hideBelow])}>
            <Skeleton className="h-3" style={{ width: c.grow ? `${50 + ((i * 13) % 35)}%` : '3.5rem' }} />
          </td>
        ))}
        {rowActions && <td className="pr-5" />}
      </tr>
    ));
  } else if (error != null) {
    body = (
      <tr>
        <td colSpan={colCount} className="px-5">
          <ErrorState error={error} onRetry={onRetry} />
        </td>
      </tr>
    );
  } else if (!rows.length) {
    body = (
      <tr>
        <td colSpan={colCount} className="px-5">
          {empty ?? <EmptyState title="Nothing here yet" />}
        </td>
      </tr>
    );
  } else if (!visible.length) {
    body = (
      <tr>
        <td colSpan={colCount} className="px-5">
          <EmptyState
            icon={<SearchX />}
            title="No matches"
            description={query ? `Nothing matches "${query.trim()}" with the current filters.` : 'Nothing matches the current filters.'}
            action={
              query ? (
                <Button size="sm" variant="ghost" onClick={() => setQuery('')}>
                  Clear search
                </Button>
              ) : undefined
            }
          />
        </td>
      </tr>
    );
  } else {
    const slice = items.slice(virt.start, virt.end);
    body = (
      <>
        {virt.padTop > 0 && (
          <tr aria-hidden style={{ height: virt.padTop }}>
            <td colSpan={colCount} className="p-0" />
          </tr>
        )}
        {slice.map((it) => {
          if (it.kind === 'group') {
            const g = it.key.slice('group:'.length);
            return (
              <tr key={it.key} className="bg-sunken/60" data-group={g} style={{ height: groupH }}>
                <td colSpan={colCount} className="overflow-hidden py-0 pl-5 pr-3">
                  {renderGroup?.(g, it.rows)}
                </td>
              </tr>
            );
          }
          const r = it.row;
          const key = it.key;
          const active = activeKey != null && key === activeKey;
          const checked = sel.isSelected(key);
          const flash = liveOrder.flashing.has(key);
          return (
            <tr
              key={key}
              tabIndex={onRowClick || selCol ? (focusRow === it.index || (focusRow == null && it.index === 0) ? 0 : -1) : undefined}
              data-row-index={it.index}
              aria-rowindex={virt.enabled ? it.index + 2 : undefined}
              aria-label={rowLabel ? rowLabel(r) : undefined}
              aria-selected={selCol ? checked : undefined}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              onKeyDown={(e) => onRowKey(e, it)}
              onFocus={(e) => e.target === e.currentTarget && setFocusRow(it.index)}
              data-active={active || undefined}
              style={{ height: rowH }}
              className={cn(
                REVEAL_GROUP,
                tableClass.rowBase,
                'group/row -outline-offset-2',
                onRowClick && 'cursor-default',
                active ? tableClass.selected : checked ? 'bg-active' : tableClass.rowHover,
                flash && !active && 'bg-accent-soft',
              )}
            >
              {selCol && (
                <td className="w-px pl-5 pr-0" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(_, ev) => sel.toggle(key, (ev.nativeEvent as MouseEvent).shiftKey)}
                    aria-label={`Select ${rowLabel ? rowLabel(r) : key}`}
                    tabIndex={-1}
                    className={cn(!sel.count && 'pointer-fine:opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100')}
                  />
                </td>
              )}
              {columns.map((c, ci) => (
                <td key={c.key} className={cellClass(c, ci)}>
                  {c.cell(r)}
                </td>
              ))}
              {rowActions && (
                <td className="w-px whitespace-nowrap py-1 pl-3 pr-5" onClick={(e) => e.stopPropagation()}>
                  <RevealSlot className="justify-end">{rowActions(r)}</RevealSlot>
                </td>
              )}
            </tr>
          );
        })}
        {virt.padBottom > 0 && (
          <tr aria-hidden style={{ height: virt.padBottom }}>
            <td colSpan={colCount} className="p-0" />
          </tr>
        )}
      </>
    );
  }

  const showLoadMore = !loading && error == null && loadMore?.more;
  const showFooter = !loading && error == null && visible.length > 0 && columns.some((c) => c.footer);
  const selectedRows = useMemo(() => (sel.count ? displayKeys.filter((k) => sel.selected.has(k)).map((k) => byKey.get(k)!) : []), [sel.count, sel.selected, displayKeys, byKey]);

  return (
    <div className={cn('flex min-w-0 flex-col', !frameless && 'overflow-clip rounded-card border border-border bg-surface hairline', className)}>
      {hasToolbar && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border-subtle px-5 py-2.5">
          {searchable && (
            <Input
              ref={searchRef}
              size="sm"
              type="search"
              aria-label={searchable.placeholder}
              placeholder={searchable.placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && query) {
                  e.preventDefault();
                  e.stopPropagation();
                  setQuery('');
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  moveTo(0);
                }
              }}
              icon={<Search />}
              trailing={searchable.shortcut && !query ? <Kbd keys={['/']} /> : undefined}
              wrapperClassName="w-full sm:w-64"
            />
          )}
          {filters && <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">{filters}</div>}
          {toolbarEnd && <div className="ml-auto flex items-center gap-2 text-caption text-fg-subtle">{typeof toolbarEnd === 'function' ? toolbarEnd(visible.length) : toolbarEnd}</div>}
        </div>
      )}
      {subbar && <div className="border-b border-border-subtle px-5 py-1.5">{subbar}</div>}
      <div className="min-w-0 [overflow-x:clip]">
        <table
          className="w-full border-collapse text-left"
          style={fixed ? { tableLayout: 'fixed' } : undefined}
          aria-label={label}
          aria-busy={loading || undefined}
          aria-rowcount={virt.enabled ? rowItems.length + 1 : undefined}
          // No aria-multiselectable: a table role does not take it (axe aria-allowed-attr); each row's checkbox says it.
        >
          {fixed && widths && (
            <colgroup>
              {selCol && <col style={{ width: widths[0] }} />}
              {columns.map((c, ci) => (
                <col key={c.key} style={c.grow ? undefined : { width: widths[ci + (selCol ? 1 : 0)] }} />
              ))}
              {rowActions && <col style={{ width: widths[columns.length + (selCol ? 1 : 0)] }} />}
            </colgroup>
          )}
          <thead className="sticky top-0 z-(--z-sticky) bg-surface">
            <tr className={tableClass.headRow} aria-rowindex={virt.enabled ? 1 : undefined}>
              {selCol && (
                <th
                  scope="col"
                  ref={(el) => {
                    headRefs.current[0] = el;
                  }}
                  className="w-px py-0 pl-5 pr-0"
                >
                  <Checkbox checked={allShownSelected} indeterminate={sel.count > 0 && !allShownSelected} onCheckedChange={() => sel.toggleAll()} aria-label={allShownSelected ? 'Clear the selection' : `Select all ${displayKeys.length}`} />
                </th>
              )}
              {columns.map((c, ci) => {
                const right = alignOf(c) === 'right';
                const sorted = sort?.key === c.key ? sort.dir : null;
                return (
                  <th
                    key={c.key}
                    ref={(el) => {
                      headRefs.current[ci + (selCol ? 1 : 0)] = el;
                    }}
                    scope="col"
                    aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={cn(
                      tableClass.head,
                      'relative py-0',
                      ci === 0 ? firstGutter : 'pl-4',
                      ci === columns.length - 1 && !rowActions && 'pr-5',
                      right && 'text-right',
                      c.hideBelow && HIDE[c.hideBelow],
                    )}
                  >
                    {c.srHeader ? (
                      <span className="sr-only">{c.header}</span>
                    ) : c.sortValue ? (
                      <TableSortButton dir={sorted} right={right} onClick={() => toggleSort(c)}>
                        {c.header}
                      </TableSortButton>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
              {rowActions && (
                <th
                  scope="col"
                  ref={(el) => {
                    headRefs.current[columns.length + (selCol ? 1 : 0)] = el;
                  }}
                  className="relative w-px pr-5"
                >
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
            {live && liveOrder.pending > 0 && (
              <tr aria-live="polite">
                <th colSpan={colCount} className="relative h-0 p-0">
                  <div className="absolute left-1/2 top-2 z-[3] -translate-x-1/2">
                    <button
                      type="button"
                      onClick={() => {
                        liveOrder.release();
                        sel.clear();
                        virt.scrollToIndex(0, 'start');
                      }}
                      className="inline-flex h-7 animate-pop-in items-center gap-1.5 rounded-full border border-accent-line bg-raised px-3 text-sm font-medium text-accent-text shadow-md transition-colors hover:bg-accent-soft"
                    >
                      <ArrowUpToLine className="size-3.5" aria-hidden />
                      {plural(liveOrder.pending, 'new row')}, show
                    </button>
                  </div>
                </th>
              </tr>
            )}
          </thead>
          <tbody ref={bodyRef} onPointerEnter={() => setPointerIn(true)} onPointerLeave={() => setPointerIn(false)}>
            {body}
          </tbody>
          {showFooter && (
            <tfoot>
              <tr className={tableClass.footRow} style={{ height: rowH }}>
                {selCol && <td />}
                {columns.map((c, ci) => (
                  // The cell's own geometry, in the footer's dim type.
                  <td key={c.key} data-column={c.key} className={cn(cellClass(c, ci), 'font-normal text-fg-subtle')}>
                    {c.footer?.(visible)}
                  </td>
                ))}
                {rowActions && <td />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {showLoadMore && loadMore && (
        <div className="flex items-center justify-end gap-2 border-t border-border-subtle px-5 py-2 text-caption text-fg-subtle">
          <span className="num">{visible.length} loaded</span>
          <Button size="sm" variant="ghost" loading={loadMore.loading} onClick={loadMore.onLoad}>
            Load older
          </Button>
        </div>
      )}
      {selection && (
        <BulkBar
          count={sel.count}
          noun={selection.noun}
          total={displayKeys.length}
          onSelectAll={sel.toggleAll}
          onClear={sel.clear}
          actions={sel.count ? selection.actions(selectedRows) : []}
        />
      )}
    </div>
  );
}
